import Head from 'next/head';
import { useTranslation } from 'next-i18next';

import { useMemo } from 'react';

import { formatHelpers, urlHelpers } from '@nocowanie/core/helpers';

import { environment } from '@app/environments/environment';
import { apiTypes } from '@app/models';
import * as accommodationStaticSlice from '@app/state/slices/accommodation-static.slice';
import { useAppSelector } from '@app/state/state';

import { RichSnippetsProps } from './rich-snippets.props';

import {
    checkIfPetsAllowed,
    createAddress,
    createAggregateRating,
    createAmenityFeatures,
    createGeo,
    createImageVariants,
    trimDescription,
} from '../json-ld.utils';

export const RichSnippets = ({ accommodationId }: RichSnippetsProps) => {
    const { t } = useTranslation('accommodation', {
        keyPrefix: 'richSnippets',
    });

    const {
        type,
        name,
        description,
        numberOfRooms,
        image,
        addressCountry,
        addressLocality,
        addressRegion,
        postalCode,
        streetAddress,
        latitude,
        longitude,
        ratingAverage,
        ratingCount,
        services,
        internalAmenityGroups,
        labels,
        cheapestRate,
        currency,
        convertedCurrency,
    } = useAppSelector(accommodationStaticSlice.selectors.selectRichSnippetsData);

    const url = useMemo(() => {
        return name && accommodationId
            ? urlHelpers.getAccommodationUrl(accommodationId, name, {}, environment.NOCOWANIE_URL)
            : undefined;
    }, [accommodationId, name]);

    const displayedType = useMemo(() => {
        switch (type) {
            case apiTypes.HotelTypeEnum['Hotel']:
                return apiTypes.HotelTypeEnum['Hotel'];
            case apiTypes.HotelTypeEnum['Hostel']:
                return apiTypes.HotelTypeEnum['Hostel'];
            case apiTypes.HotelTypeEnum['Motel']:
                return apiTypes.HotelTypeEnum['Motel'];
            default:
                return 'LodgingBusiness';
        }
    }, [type]);

    const imageVariants = image ? createImageVariants(image) : undefined;

    const trimmedDesc = description ? trimDescription(description) : undefined;

    const price = useMemo(() => {
        const rate = cheapestRate?.convertedRate ? cheapestRate?.convertedRate : cheapestRate?.rate;

        // if we have more than one guest, we divide the price by their (guests) number
        return rate && cheapestRate?.guests && cheapestRate.guests > 1
            ? Math.round(rate / cheapestRate.guests)
            : rate;
    }, [cheapestRate]);

    // convertedCurrency isn't in ElasticSearch, so we set the default value
    const convertedCurrencyCode = convertedCurrency ? convertedCurrency : 'PLN';

    // we need to remap 'PLN' to 'zł'
    const formattedCurrency = formatHelpers.price(0, convertedCurrencyCode);
    const displayedConvertedCurrency = formattedCurrency.trim().split(/\s+/).pop();

    const displayedCurrency = cheapestRate?.convertedRate ? displayedConvertedCurrency : currency;

    const petsAllowed = checkIfPetsAllowed({
        labels: labels ?? [],
        internalAmenityGroups: internalAmenityGroups ?? [],
        services: services ?? [],
    });
    const address = createAddress({
        addressCountry,
        addressLocality,
        addressRegion,
        postalCode,
        streetAddress,
    });
    const geo = latitude && longitude ? createGeo({ latitude, longitude }) : null;
    const aggregateRating =
        ratingAverage && ratingCount ? createAggregateRating({ ratingAverage, ratingCount }) : null;
    const amenityFeature = services ? createAmenityFeatures({ services }) : null;

    if (!type || !name) return;

    const schemaObj = {
        '@context': 'https://schema.org/',
        '@type': displayedType,
        name,
        ...(trimmedDesc ? { description: trimmedDesc } : null),
        ...(numberOfRooms ? { numberOfRooms: numberOfRooms } : null),
        ...(url ? { tourBookingPage: url } : null),
        petsAllowed,
        ...(imageVariants ? { image: imageVariants } : null),
        ...(address ? { address } : null),
        ...(geo ? { geo } : null),
        ...(aggregateRating ? { aggregateRating } : null),
        ...(amenityFeature ? { amenityFeature } : null),
        ...(latitude && longitude
            ? { hasMap: `https://www.openstreetmap.org/#map=15/${latitude}/${longitude}` }
            : null),
        ...{
            priceRange:
                price && currency
                    ? t('cheapestPrice', { value: price, value2: displayedCurrency })
                    : t('noPriceAvailable'),
        },
    };

    return (
        <Head>
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaObj) }}
            />
        </Head>
    );
};
